import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppSelect from '@nxsys/components/AppSelect';
import {
  PaymentFrequencyEnum,
  TimesheetStatusEnum,
} from '@nxsys/constants/AppEnums';
import {
  useBulkChangeTaxPeriod,
  useChangeTaxPeriod,
} from '@nxsys/hooks/api/timesheets';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { PaymentFrequencyType } from '@nxsys/types/models/Common';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { showMessage } from '@nxsys/utils/message';
import { getOptionsAllTaxWeekTaxMonth } from '@nxsys/utils/taxYear';
import { Form, Radio } from 'antd';
import TextArea from 'antd/lib/input/TextArea';
import React, { useEffect, useMemo, useState } from 'react';

type SelectOption = { label: string; value: number };

type FormValues = {
  taxPeriod?: SelectOption | null;
  reason?: string;
  skipReapproval?: boolean;
};

type BulkResult = {
  updated: string[];
  failed: { id: string; reason: string }[];
};

type ChangeTaxPeriodModalProps = {
  isOpen: boolean;
  toggle: () => void;
  // 1 timesheet -> single change; >1 -> bulk. Caller must ensure a uniform
  // pay frequency (a mixed selection is blocked before opening this modal).
  timesheets: TimeSheetDataItem[];
  onSuccess: () => void;
};

const getPayFrequency = (ts?: TimeSheetDataItem): PaymentFrequencyType =>
  (ts?.subcontractorRef?.payFrequency ??
    ts?.payFrequency ??
    PaymentFrequencyEnum.WEEKLY) as PaymentFrequencyType;

const ChangeTaxPeriodModal: React.FC<ChangeTaxPeriodModalProps> = ({
  isOpen,
  toggle,
  timesheets,
  onSuccess,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const [form] = Form.useForm<FormValues>();

  const { run: changeTaxPeriod, loading: isChanging } = useChangeTaxPeriod();
  const { run: bulkChangeTaxPeriod, loading: isBulkChanging } =
    useBulkChangeTaxPeriod();

  const [result, setResult] = useState<BulkResult | null>(null);

  const isBulk = timesheets.length > 1;
  const payFrequency = getPayFrequency(timesheets[0]);
  const isMonthly = payFrequency === PaymentFrequencyEnum.MONTHLY;

  // NX-5237 (Victor 28/08 addendum): the "send back / update without re-approval"
  // choice only applies to a GATED timesheet — already approved AND running an
  // approval workflow (timesheetApprovalNeeded = 'TRUE'). Auto-approved rows
  // change in place with no prompt (mirrors the BE resolveApprovalChoice gate).
  const hasApproved = timesheets.some(
    (t) =>
      t.approveStatus === TimesheetStatusEnum.APPROVED &&
      t.timesheetApprovalNeeded === 'TRUE',
  );

  // NX-5237 (Victor 31/08): a reason is mandatory only for a GATED selection
  // (hasApproved) — the BE enforces the same via resolveApprovalChoice. The
  // approval-stage tiles no longer force a reason for a non-gated change.
  const showReason = hasApproved;

  const periodOptions = useMemo<SelectOption[]>(
    () => getOptionsAllTaxWeekTaxMonth(payFrequency),
    [payFrequency],
  );

  // Pre-fill the period: single -> current period; bulk -> blank. The tax year
  // is never changed (kept as each timesheet's current year — AC #3 dropped).
  useEffect(() => {
    if (!isOpen) return;
    setResult(null);
    const first = timesheets[0];
    const currentPeriod = isMonthly
      ? first?.monthConfig
      : first?.taxPeriodConfig;
    form.setFieldsValue({
      taxPeriod:
        !isBulk && currentPeriod
          ? (periodOptions.find((o) => o.value === currentPeriod) ?? null)
          : null,
      reason: undefined,
      // NX-5237 (Victor 27/08): no preselect — when the selection is approved
      // the user must make an explicit choice (mirrors the BE 400).
      skipReapproval: undefined,
    });
  }, [isOpen]);

  const onFinish = (values: FormValues) => {
    const taxPeriod = values.taxPeriod?.value;
    if (!taxPeriod) return;

    if (isBulk) {
      bulkChangeTaxPeriod({
        ids: timesheets.map((t) => t.id),
        taxPeriod,
        reason: values.reason,
        skipReapproval: values.skipReapproval,
      })
        .then((res) => {
          onSuccess();
          if (res?.failed?.length) {
            // Skip-and-report: keep the modal open to show which rows failed.
            setResult({ updated: res.updated ?? [], failed: res.failed });
          } else {
            showMessage({
              type: 'success',
              content: messages['timesheet.changeTaxPeriodSuccess'],
            });
            toggle();
          }
        })
        // The error toast is surfaced by the request interceptor; swallow the
        // rejection so it isn't an unhandled promise rejection (modal stays open).
        .catch(() => undefined);
    } else {
      changeTaxPeriod({
        id: timesheets[0].id,
        taxPeriod,
        reason: values.reason,
        skipReapproval: values.skipReapproval,
      })
        .then(() => {
          showMessage({
            type: 'success',
            content: messages['timesheet.changeTaxPeriodSuccess'],
          });
          onSuccess();
          toggle();
        })
        // The error toast is surfaced by the request interceptor; swallow the
        // rejection so it isn't an unhandled promise rejection (modal stays open).
        .catch(() => undefined);
    }
  };

  const isPending = isChanging || isBulkChanging;

  const title = isBulk
    ? formatMessage(
        { id: 'timesheet.changeTaxPeriodBulkTitle' },
        { count: timesheets.length },
      )
    : messages['timesheet.changeTaxPeriod'];

  const actions = result
    ? [
        {
          name: messages['common.close'],
          buttonType: 'primary' as const,
          onClick: toggle,
        },
      ]
    : [
        {
          name: messages['common.cancel'],
          variant: 'outlined' as const,
          onClick: toggle,
        },
        {
          name: messages['common.confirm'],
          buttonType: 'primary' as const,
          onClick: () => form.submit(),
          loading: isPending,
          disabled: isPending,
        },
      ];

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={actions}
      minHeight="200px"
    >
      {result ? (
        <div>
          <p className="mb-3">
            {formatMessage(
              { id: 'timesheet.changeTaxPeriodResultSummary' },
              {
                updated: result.updated.length,
                failed: result.failed.length,
              },
            )}
          </p>
          {result.failed.length > 0 && (
            <ul className="flex flex-col gap-y-1">
              {result.failed.map((item) => (
                <li key={item.id}>
                  <strong>{item.id}</strong>: {item.reason}
                </li>
              ))}
            </ul>
          )}
        </div>
      ) : (
        <AppFormContainer
          form={form}
          onFinish={onFinish}
          layout="vertical"
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
        >
          <Form.Item
            name="taxPeriod"
            label={
              isMonthly
                ? messages['common.taxMonth']
                : messages['common.taxPeriod']
            }
            rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
          >
            <AppSelect
              options={periodOptions}
              placeholder={messages['form.select']}
            />
          </Form.Item>
          {showReason && (
            <Form.Item
              name="reason"
              label={messages['common.reason']}
              rules={[
                { required: true, message: messages['form.pleaseEnter'] },
              ]}
            >
              <TextArea rows={4} placeholder={messages['common.enter']} />
            </Form.Item>
          )}
          {hasApproved && (
            <Form.Item
              name="skipReapproval"
              label={messages['timesheet.reapprovalChoiceLabel']}
              rules={[
                {
                  // `false` (send back) is a valid pick and passes required;
                  // only an untouched (undefined) choice is rejected.
                  required: true,
                  message: messages['timesheet.reapprovalChoiceRequired'],
                },
              ]}
            >
              <Radio.Group>
                <Radio value={false}>
                  {messages['timesheet.sendBackForReapproval']}
                </Radio>
                <Radio value={true}>
                  {messages['timesheet.skipReapproval']}
                </Radio>
              </Radio.Group>
            </Form.Item>
          )}
        </AppFormContainer>
      )}
    </AppModalContainer>
  );
};

export default ChangeTaxPeriodModal;
